import React, { useState } from 'react';
import { useSoundNotificationsContext } from '../../contexts/SoundNotificationsContext';
import './DebugPanel.css';

function DebugPanel({ onSimulateTrade, onSimulateLiquidation, onSubscribe, onUnsubscribe, isConnected, isSubscribed }) {
  const { playTieredSound } = useSoundNotificationsContext();

  const [tradeForm, setTradeForm] = useState({
    symbol: 'BTCUSDT',
    exchange: 'BINANCE',
    side: 'buy',
    dollarSize: 500000,
    price: 95000
  });

  const [liquidationForm, setLiquidationForm] = useState({
    symbol: 'ETHUSDT',
    exchange: 'BINANCE_FUTURES',
    side: 'sell',
    dollarSize: 100000,
    price: 3200
  });

  const handleTestSound = (tier) => {
    console.log(`🧪 Testing sound tier ${tier}`);

    // Convert tier to appropriate dollar amount for testing
    const dollarAmounts = {
      1: 5_000_000,      // $5M - Tier 1 range ($1M - $25M)
      2: 30_000_000,     // $30M - Tier 2 range ($25M - $50M)
      3: 75_000_000,     // $75M - Tier 3 range ($50M - $100M)
      4: 150_000_000     // $150M - Tier 4 range ($100M+)
    };

    playTieredSound(dollarAmounts[tier], 'whale');
  };

  // Accept custom data or use form state
  const handleSimulateTrade = (customData = null) => {
    const data = customData || tradeForm;
    const trade = {
      symbol: data.symbol,
      exchange: data.exchange,
      side: data.side,
      size: data.dollarSize / data.price,
      price: data.price,
      dollarSize: data.dollarSize,
      timestamp: new Date().toISOString(),
      isSimulated: true
    };
    console.log('🧪 Simulating whale trade:', trade);
    onSimulateTrade(trade);
  };

  // Accept custom data or use form state
  const handleSimulateLiquidation = (customData = null) => {
    const data = customData || liquidationForm;
    const liquidation = {
      symbol: data.symbol,
      exchange: data.exchange,
      side: data.side,
      size: data.dollarSize / data.price,
      price: data.price,
      dollarSize: data.dollarSize,
      isLiquidation: true,
      timestamp: new Date().toISOString(),
      isSimulated: true
    };
    console.log('🧪 Simulating liquidation:', liquidation);
    onSimulateLiquidation(liquidation);
  };

  return (
    <div className="debug-panel">
      <div className="debug-header">
        <h3>🧪 Debug Panel (Test Mode)</h3>
        <div className="debug-status">
          <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
            {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
          </span>
          <span className={`status-indicator ${isSubscribed ? 'subscribed' : 'unsubscribed'}`}>
            {isSubscribed ? '✅ Subscribed' : '❌ Not Subscribed'}
          </span>
        </div>
      </div>

      <div className="debug-section">
        <h4>🔊 Sound Tests</h4>
        <div className="button-grid">
          <button onClick={() => handleTestSound(1)} className="debug-btn sound-btn tier1">
            Tier 1 ($1M - $25M)
          </button>
          <button onClick={() => handleTestSound(2)} className="debug-btn sound-btn tier2">
            Tier 2 ($25M - $50M)
          </button>
          <button onClick={() => handleTestSound(3)} className="debug-btn sound-btn tier3">
            Tier 3 ($50M - $100M)
          </button>
          <button onClick={() => handleTestSound(4)} className="debug-btn sound-btn tier4">
            Tier 4 ($100M+)
          </button>
        </div>
      </div>

      <div className="debug-section">
        <h4>🔌 WebSocket Controls</h4>
        <div className="button-grid">
          <button
            onClick={onSubscribe}
            className="debug-btn ws-btn subscribe"
            disabled={isSubscribed}
          >
            Subscribe to Feed
          </button>
          <button
            onClick={onUnsubscribe}
            className="debug-btn ws-btn unsubscribe"
            disabled={!isSubscribed}
          >
            Unsubscribe from Feed
          </button>
        </div>
      </div>

      <div className="debug-section">
        <h4>🐋 Simulate Whale Trade</h4>
        <div className="form-grid">
          <div className="form-group">
            <label>Symbol</label>
            <input
              type="text"
              value={tradeForm.symbol}
              onChange={(e) => setTradeForm({ ...tradeForm, symbol: e.target.value })}
              placeholder="BTCUSDT"
            />
          </div>
          <div className="form-group">
            <label>Exchange</label>
            <input
              type="text"
              value={tradeForm.exchange}
              onChange={(e) => setTradeForm({ ...tradeForm, exchange: e.target.value })}
              placeholder="BINANCE"
            />
          </div>
          <div className="form-group">
            <label>Side</label>
            <select
              value={tradeForm.side}
              onChange={(e) => setTradeForm({ ...tradeForm, side: e.target.value })}
            >
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
          </div>
          <div className="form-group">
            <label>Dollar Size ($)</label>
            <input
              type="number"
              value={tradeForm.dollarSize}
              onChange={(e) => setTradeForm({ ...tradeForm, dollarSize: Number(e.target.value) })}
              step="1000"
            />
          </div>
          <div className="form-group">
            <label>Price ($)</label>
            <input
              type="number"
              value={tradeForm.price}
              onChange={(e) => setTradeForm({ ...tradeForm, price: Number(e.target.value) })}
              step="0.01"
            />
          </div>
        </div>
        <button onClick={handleSimulateTrade} className="debug-btn simulate-btn trade">
          📈 Simulate Whale Trade
        </button>
      </div>

      <div className="debug-section">
        <h4>🔥 Simulate Liquidation</h4>
        <div className="form-grid">
          <div className="form-group">
            <label>Symbol</label>
            <input
              type="text"
              value={liquidationForm.symbol}
              onChange={(e) => setLiquidationForm({ ...liquidationForm, symbol: e.target.value })}
              placeholder="ETHUSDT"
            />
          </div>
          <div className="form-group">
            <label>Exchange</label>
            <input
              type="text"
              value={liquidationForm.exchange}
              onChange={(e) => setLiquidationForm({ ...liquidationForm, exchange: e.target.value })}
              placeholder="BINANCE_FUTURES"
            />
          </div>
          <div className="form-group">
            <label>Side</label>
            <select
              value={liquidationForm.side}
              onChange={(e) => setLiquidationForm({ ...liquidationForm, side: e.target.value })}
            >
              <option value="buy">Long Liquidation</option>
              <option value="sell">Short Liquidation</option>
            </select>
          </div>
          <div className="form-group">
            <label>Dollar Size ($)</label>
            <input
              type="number"
              value={liquidationForm.dollarSize}
              onChange={(e) => setLiquidationForm({ ...liquidationForm, dollarSize: Number(e.target.value) })}
              step="1000"
            />
          </div>
          <div className="form-group">
            <label>Price ($)</label>
            <input
              type="number"
              value={liquidationForm.price}
              onChange={(e) => setLiquidationForm({ ...liquidationForm, price: Number(e.target.value) })}
              step="0.01"
            />
          </div>
        </div>
        <button onClick={handleSimulateLiquidation} className="debug-btn simulate-btn liquidation">
          💥 Simulate Liquidation
        </button>
      </div>

      <div className="debug-section">
        <h4>📋 Tier Presets</h4>

        {/* Tier 1: $1M - $25M */}
        <div className="preset-tier-group">
          <h5>🐟 Tier 1 ($1M - $25M)</h5>
          <div className="button-grid">
            <button
              onClick={() => {
                const preset = { symbol: 'BTCUSDT', exchange: 'BINANCE', side: 'buy', dollarSize: 5000000, price: 95000 };
                handleSimulateTrade(preset);
              }}
              className="debug-btn preset-btn tier1"
            >
              🐋 Trade LONG
            </button>
            <button
              onClick={() => {
                const preset = { symbol: 'BTCUSDT', exchange: 'BINANCE', side: 'sell', dollarSize: 5000000, price: 95000 };
                handleSimulateTrade(preset);
              }}
              className="debug-btn preset-btn tier1"
            >
              🐋 Trade SHORT
            </button>
            <button
              onClick={() => {
                const preset = { symbol: 'ETHUSDT', exchange: 'BINANCE_FUTURES', side: 'buy', dollarSize: 5000000, price: 3200 };
                handleSimulateLiquidation(preset);
              }}
              className="debug-btn preset-btn tier1"
            >
              💥 Liq LONG
            </button>
            <button
              onClick={() => {
                const preset = { symbol: 'ETHUSDT', exchange: 'BINANCE_FUTURES', side: 'sell', dollarSize: 5000000, price: 3200 };
                handleSimulateLiquidation(preset);
              }}
              className="debug-btn preset-btn tier1"
            >
              💥 Liq SHORT
            </button>
          </div>
        </div>

        {/* Tier 2: $25M - $50M */}
        <div className="preset-tier-group">
          <h5>🐠 Tier 2 ($25M - $50M)</h5>
          <div className="button-grid">
            <button
              onClick={() => {
                const preset = { symbol: 'BTCUSDT', exchange: 'BINANCE', side: 'buy', dollarSize: 30000000, price: 95000 };
                handleSimulateTrade(preset);
              }}
              className="debug-btn preset-btn tier2"
            >
              🐋 Trade LONG
            </button>
            <button
              onClick={() => {
                const preset = { symbol: 'BTCUSDT', exchange: 'BINANCE', side: 'sell', dollarSize: 30000000, price: 95000 };
                handleSimulateTrade(preset);
              }}
              className="debug-btn preset-btn tier2"
            >
              🐋 Trade SHORT
            </button>
            <button
              onClick={() => {
                const preset = { symbol: 'ETHUSDT', exchange: 'BINANCE_FUTURES', side: 'buy', dollarSize: 30000000, price: 3200 };
                handleSimulateLiquidation(preset);
              }}
              className="debug-btn preset-btn tier2"
            >
              💥 Liq LONG
            </button>
            <button
              onClick={() => {
                const preset = { symbol: 'ETHUSDT', exchange: 'BINANCE_FUTURES', side: 'sell', dollarSize: 30000000, price: 3200 };
                handleSimulateLiquidation(preset);
              }}
              className="debug-btn preset-btn tier2"
            >
              💥 Liq SHORT
            </button>
          </div>
        </div>

        {/* Tier 3: $50M - $100M */}
        <div className="preset-tier-group">
          <h5>🐬 Tier 3 ($50M - $100M)</h5>
          <div className="button-grid">
            <button
              onClick={() => {
                const preset = { symbol: 'BTCUSDT', exchange: 'BINANCE', side: 'buy', dollarSize: 75000000, price: 95000 };
                handleSimulateTrade(preset);
              }}
              className="debug-btn preset-btn tier3"
            >
              🐋 Trade LONG
            </button>
            <button
              onClick={() => {
                const preset = { symbol: 'BTCUSDT', exchange: 'BINANCE', side: 'sell', dollarSize: 75000000, price: 95000 };
                handleSimulateTrade(preset);
              }}
              className="debug-btn preset-btn tier3"
            >
              🐋 Trade SHORT
            </button>
            <button
              onClick={() => {
                const preset = { symbol: 'SOLUSDT', exchange: 'BINANCE_FUTURES', side: 'buy', dollarSize: 75000000, price: 150 };
                handleSimulateLiquidation(preset);
              }}
              className="debug-btn preset-btn tier3"
            >
              💥 Liq LONG
            </button>
            <button
              onClick={() => {
                const preset = { symbol: 'SOLUSDT', exchange: 'BINANCE_FUTURES', side: 'sell', dollarSize: 75000000, price: 150 };
                handleSimulateLiquidation(preset);
              }}
              className="debug-btn preset-btn tier3"
            >
              💥 Liq SHORT
            </button>
          </div>
        </div>

        {/* Tier 4: $100M+ */}
        <div className="preset-tier-group">
          <h5>🐋 Tier 4 ($100M+)</h5>
          <div className="button-grid">
            <button
              onClick={() => {
                const preset = { symbol: 'BTCUSDT', exchange: 'BINANCE', side: 'buy', dollarSize: 150000000, price: 95000 };
                handleSimulateTrade(preset);
              }}
              className="debug-btn preset-btn tier4"
            >
              🐋 Trade LONG
            </button>
            <button
              onClick={() => {
                const preset = { symbol: 'BTCUSDT', exchange: 'BINANCE', side: 'sell', dollarSize: 150000000, price: 95000 };
                handleSimulateTrade(preset);
              }}
              className="debug-btn preset-btn tier4"
            >
              🐋 Trade SHORT
            </button>
            <button
              onClick={() => {
                const preset = { symbol: 'BTCUSDT', exchange: 'BYBIT', side: 'buy', dollarSize: 150000000, price: 95000 };
                handleSimulateLiquidation(preset);
              }}
              className="debug-btn preset-btn tier4"
            >
              💥 Liq LONG
            </button>
            <button
              onClick={() => {
                const preset = { symbol: 'BTCUSDT', exchange: 'BYBIT', side: 'sell', dollarSize: 150000000, price: 95000 };
                handleSimulateLiquidation(preset);
              }}
              className="debug-btn preset-btn tier4"
            >
              💥 Liq SHORT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DebugPanel;
